










































































































import { computed, defineComponent, PropType, ref, watchEffect } from "@vue/composition-api";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { ProfileLink } from "@scrile/api-provider/dist/api/ProfileLinksProvider";
import { TagInfo } from "@scrile/api-provider/dist/api/TagsProvider";
import {
  ContentVerification,
  ContentVerificationFileType,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentVerificationProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import { $t } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getQuestionaryInfoFields } from "@/lib/questionaryFieldsHelper";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { FormFields, FormValues } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseForm from "@/components/BaseForm";
import BaseIcon from "@/components/BaseIcon";
import BaseMenu from "@/components/BaseMenu/BaseMenu.vue";
import BasePage from "@/components/BasePage";
import BaseSpinner from "@/components/BaseSpinner/BaseSpinner.vue";
import PageTitle from "@/components/PageTitle";
import ProfileHeaderAvatar from "@/components/ProfileHeaderAvatar";
import ProfileHeaderBackground from "@/components/ProfileHeaderBackground";
import SafeForWorkStatusSettings from "@/components/SafeForWorkStatusSettings";
import SelectUserTagsModal from "@/components/SelectUserTagsModal";
import AvatarCropModal from "./AvatarCropModal.vue";
import ImageUpload from "./ImageUpload.vue";
import PersonalEditLocalization from "./PersonalEditLocalization.vue";
import ProfileTagsView from "./ProfileTagsView.vue";
import { IFileUploadingType, linkFields, ProfileFileType } from "../const";

const translateLabelsInFields = (fields: FormFields): FormFields => {
  return fields.map(i => ({ ...i, label: i.label && $t(i.label) }));
};

export default defineComponent({
  name: "PageProfileEditView",
  components: {
    BaseSpinner,
    BaseMenu,
    PersonalEditLocalization,
    AvatarCropModal,
    SelectUserTagsModal,
    ProfileTagsView,
    ImageUpload,
    ProfileHeaderAvatar,
    ProfileHeaderBackground,
    BaseForm,
    BasePage,
    BaseIcon,
    PageTitle,
    BaseButton,
    SafeForWorkStatusSettings,
  },
  props: {
    user: { type: Object as PropType<UserPublicData>, required: true },
    info: { type: Object as PropType<Record<string, string>>, required: true },
    files: { type: Array as PropType<FileData[]>, required: true },
    loading: { type: Boolean },
    userTags: { type: Array as PropType<TagInfo[]> },
    fileLoading: { type: Object as PropType<IFileUploadingType> },
    avatarVerification: { type: Object as PropType<ContentVerification | null> },
    backgroundVerification: { type: Object as PropType<ContentVerification | null> },
    profileLinks: { type: Array as PropType<ProfileLink[]>, required: true },
  },
  setup(props, { root, emit }) {
    const { isMember } = useAuthUser();

    const showUserTagsModal = ref(false);
    const showAvatarCropModal = ref(false);
    const isValid = ref(true);
    const newAvatarFile = ref<File | null>();
    const newAvatarSrc = ref("");
    const hasBecomeCreator = computed(() => isMember.value && !config.featureSoloModel);
    const restrictedMember = computed(() => config.featureRestrictMember && props.user.role === "member");
    const isUserCreator = computed(() => checkRole(props.user.role ?? "", "creator"));
    const showLocalization = computed(() => !config.featureHideLocalization);
    const showSafeForWorkSetup = computed(() => config.featureNSFW && isUserCreator.value);
    const isAvatarLoading = computed(
      () =>
        props.fileLoading?.type &&
        [ProfileFileType.AVATAR, ProfileFileType.AVATAR_VERIFICATION].includes(props.fileLoading.type) &&
        props.fileLoading.loading
    );
    const isBackgroundLoading = computed(
      () =>
        props.fileLoading?.type &&
        [ProfileFileType.BACKGROUND, ProfileFileType.BACKGROUND_VERIFICATION].includes(props.fileLoading.type) &&
        props.fileLoading.loading
    );

    const questionaryUserFields = computed(() => {
      const fields = getQuestionaryInfoFields().filter(
        i => !i.fieldMeta?.social && !i.fieldMeta?.amazon && !i.fieldMeta?.calendar
      );
      return translateLabelsInFields(fields);
    });

    const socialFields = computed(() => (isUserCreator.value ? linkFields : []));

    const amazonFields = computed<FormFields>(() => {
      const fields = isUserCreator.value ? getQuestionaryInfoFields().filter(i => i.fieldMeta?.amazon) : [];
      return translateLabelsInFields(fields);
    });

    const calendarFields = computed<FormFields>(() => {
      const fields = isUserCreator.value ? getQuestionaryInfoFields().filter(i => i.fieldMeta?.calendar) : [];
      return translateLabelsInFields(fields);
    });

    const userFields = computed<FormFields>(() => {
      const fields = restrictedMember.value
        ? questionaryUserFields.value.filter(i => i.fieldMeta?.allowedForMember)
        : questionaryUserFields.value;
      return [
        {
          name: "screenName",
          label: $t("Name"),
          rules: ["required", "max:30"],
          maxlength: "30",
        },
        {
          name: "username",
          label: $t("Username"),
          rules: {
            required: true,
            regex: /^[a-zA-Z0-9_.-]*$/,
            max: 30,
          },
          maxlength: "30",
          rulesMessages: {
            regex: $t("Names can only contain letters, numbers, dashes, dots, and underscores."),
          },
          hint: $t("Names can only contain letters, numbers, dashes, dots, and underscores."),
        },
        ...fields,
      ];
    });

    const userFieldsValues = ref<FormValues>({
      screenName: props.user.screenName,
      username: props.user.username,
      ...questionaryUserFields.value.reduce<FormValues>((out, item) => {
        out[item.name] = props.info[item.name];
        return out;
      }, {}),
    });

    const socialFieldsValues = ref<FormValues>({
      ...socialFields.value.reduce<FormValues>((out, item) => {
        out[item.name] = props.profileLinks.find(i => i.type === item.name)?.url ?? "";
        return out;
      }, {}),
    });

    const amazonValues = ref<FormValues>({
      ...amazonFields.value.reduce<FormValues>((out, item) => {
        out[item.name] = props.info[item.name];
        return out;
      }, {}),
    });

    const calendarValues = ref<FormValues>({
      ...calendarFields.value.reduce<FormValues>((out, item) => {
        out[item.name] = props.info[item.name];
        return out;
      }, {}),
    });

    const localizationValues = ref<FormValues>({});

    const allowThemes = computed(() => config.featureTags);

    const editLocalization = (value: FormValues) => {
      if (value.localizationCountryCode) {
        localizationValues.value.localizationCity = value.localizationCity;
        localizationValues.value.localizationCountryCode = value.localizationCountryCode;
        localizationValues.value.localizationStateCode = value.localizationStateCode;
      }

      if (value.localizationLanguage) {
        localizationValues.value.localizationLanguage = value.localizationLanguage;
      }
    };

    const showAvatarMenu = computed(() => !!props.user.avatar || !!props.avatarVerification);
    const showBackgroundMenu = computed(() => !!props.user.background || !!props.backgroundVerification);

    const getDeleteButtonText = (verification?: ContentVerification | null) =>
      !!verification ? $t("Cancel verification") : $t("Delete");

    const getImageUrl = (type: string) => {
      const file = props.files.find(file => file.fileId.type === type && file.fileId.subjectId === props.user.id);
      return file ? MediaFilesController.getImageThumbnail(file.urlPart, "1080p") : "";
    };

    const avatarVerificationSrc = computed(() =>
      props.avatarVerification
        ? MediaFilesController.getImageThumbnail(props.avatarVerification.details.file.urlPart, 224)
        : undefined
    );

    const background = computed(() =>
      props.backgroundVerification
        ? MediaFilesController.getImageThumbnail(props.backgroundVerification.details.file.urlPart, "1080p")
        : getImageUrl("background_image")
    );

    const onBackClick = () => {
      root.$router.push(getProfileUrl(props.user));
    };

    const onUserTagsSave = (tags: string[]) => {
      showUserTagsModal.value = false;
      emit("updateUserTags", tags);
    };

    const onValidation = (valid: boolean) => {
      isValid.value = valid;
    };

    const onAvatarUploadClick = (fileList: FileList) => {
      newAvatarFile.value = fileList[0];
      showAvatarCropModal.value = true;
    };

    const onAvatarCropClose = () => {
      showAvatarCropModal.value = false;
      newAvatarFile.value = null;
      newAvatarSrc.value = "";
    };

    const onImageCropped = (file: File) => {
      emit("upload", [file], ProfileFileType.AVATAR_VERIFICATION, () => (showAvatarCropModal.value = false));
    };

    const fileReader = new FileReader();
    fileReader.onload = event => {
      if (event && event.target) {
        newAvatarSrc.value = event.target.result as string;
      }
    };

    watchEffect(() => {
      if (newAvatarFile.value) {
        fileReader.readAsDataURL(newAvatarFile.value);
      } else {
        newAvatarFile.value = null;
      }
    });

    const save = () => {
      emit("save", {
        socialFields: socialFieldsValues.value,
        ...userFieldsValues.value,
        ...amazonValues.value,
        ...calendarValues.value,
        ...localizationValues.value,
      });
    };

    return {
      isValid,
      restrictedMember,
      hasBecomeCreator,
      userFieldsValues,
      amazonValues,
      calendarValues,
      socialFieldsValues,
      userFields,
      socialFields,
      amazonFields,
      calendarFields,
      showUserTagsModal,
      allowThemes,
      background,
      showAvatarCropModal,
      newAvatarSrc,
      localizationValues,
      showLocalization,
      showSafeForWorkSetup,
      showAvatarMenu,
      showBackgroundMenu,
      avatarVerificationSrc,
      ContentVerificationFileType,
      isAvatarLoading,
      isBackgroundLoading,
      getDeleteButtonText,
      onAvatarCropClose,
      onBackClick,
      onAvatarUploadClick,
      onImageCropped,
      onUserTagsSave,
      onValidation,
      save,
      editLocalization,
      ProfileFileType,
    };
  },
});
